/**
 * Browser side of the prototype build-stream error contract
 * (PROTOTYPER_STABILITY_TECH_PLAN.md §5).
 *
 * A failure reaches the browser in one of two shapes:
 * - before the stream starts: a non-2xx HTTP response with `{ code, message, error }`
 *   (Nest forwards Flask's status and body). `fetchBuildStream` turns it into a
 *   `BuildStreamHttpError`, which `useChat` hands to `onError`.
 * - after the stream started: a `data-prototyperError` part (`{ code, message }`)
 *   followed by the AI SDK `error` chunk. `parseBuildStreamErrorData` reads the part.
 *
 * `retryPolicyFor` decides whether the workbench may retry: credits, ownership,
 * validation, context-size, session and fix-limit problems never change by retrying.
 *
 * Server copies of the code list: ideoz-ai-api `src/app/prototyper/build_stream_errors.py`
 * and ideoz-backend `src/modules/prototyper/utils/build-stream-error.ts`.
 */

export type BuildStreamErrorCode =
  | 'INSUFFICIENT_CREDITS'
  | 'PLAN_NOT_READY'
  | 'PROJECT_BUSY'
  | 'NOT_OWNER'
  | 'CHEF_UNAVAILABLE'
  | 'CONTEXT_OVERFLOW'
  | 'TIMEOUT'
  | 'VALIDATION_ERROR'
  | 'INTERNAL'
  /** An automatic fix stream found no attempt left; the card asks the founder. */
  | 'FIX_LIMIT_REACHED'
  /** Browser-only: a 401 from Nest means the access token expired. */
  | 'SESSION_EXPIRED';

export type BuildStreamError = {
  code: BuildStreamErrorCode;
  message: string;
  status?: number;
};

export type BuildStreamRetryPolicy = 'none' | 'backoff';

const KNOWN_CODES: ReadonlySet<string> = new Set<BuildStreamErrorCode>([
  'INSUFFICIENT_CREDITS',
  'PLAN_NOT_READY',
  'PROJECT_BUSY',
  'NOT_OWNER',
  'CHEF_UNAVAILABLE',
  'CONTEXT_OVERFLOW',
  'TIMEOUT',
  'VALIDATION_ERROR',
  'INTERNAL',
  'FIX_LIMIT_REACHED',
  'SESSION_EXPIRED',
]);

/** How many times one turn may retry a failed build-stream request. */
export const MAX_BUILD_STREAM_TRANSPORT_RETRIES = 2;

const USER_MESSAGES: Record<BuildStreamErrorCode, string> = {
  INSUFFICIENT_CREDITS: "You don't have enough credits to build this prototype.",
  PLAN_NOT_READY: 'The prototype plan is still being saved. Try again in a moment.',
  PROJECT_BUSY: 'The previous build is still finishing. Try again in a moment.',
  NOT_OWNER: "You don't have access to this prototype.",
  CHEF_UNAVAILABLE: 'The prototype builder stopped responding. Try again.',
  CONTEXT_OVERFLOW: 'This request is too large for the model. Try a shorter request.',
  TIMEOUT: 'The prototype build took too long. Try again.',
  VALIDATION_ERROR: 'The build request was invalid.',
  INTERNAL: 'The prototype build failed. Try again.',
  FIX_LIMIT_REACHED: 'Automatic fixes stopped for this build. Choose what to do next.',
  SESSION_EXPIRED: 'Your session expired. Refresh the page and try again.',
};

export function isBuildStreamErrorCode(value: unknown): value is BuildStreamErrorCode {
  return typeof value === 'string' && KNOWN_CODES.has(value);
}

export class BuildStreamHttpError extends Error {
  readonly status: number;
  readonly code: BuildStreamErrorCode;

  constructor(status: number, code: BuildStreamErrorCode, message: string) {
    super(message);
    this.name = 'BuildStreamHttpError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Fallback when the response carried no `code` (an older server, or a Nest guard such as
 * `SubscriptionLockGuard` that throws before the proxy runs). `hint` is the body text,
 * used only to tell the meanings of 403 and 422 apart.
 */
export function codeForHttpStatus(status: number, hint: string): BuildStreamErrorCode {
  switch (status) {
    case 401:
      return 'SESSION_EXPIRED';
    case 403:
      return /credit|subscription|upgrade|billing/i.test(hint)
        ? 'INSUFFICIENT_CREDITS'
        : 'NOT_OWNER';
    case 409:
      return 'PROJECT_BUSY';
    case 422:
      return /not allowed|not eligible|timed out waiting/i.test(hint)
        ? 'PLAN_NOT_READY'
        : 'VALIDATION_ERROR';
    case 429:
    case 502:
    case 503:
      return 'CHEF_UNAVAILABLE';
    case 504:
      return 'TIMEOUT';
    default:
      return 'INTERNAL';
  }
}

function firstString(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim();
  // Nest's ValidationPipe sends `message` as an array of strings.
  if (Array.isArray(value)) {
    const joined = value
      .filter((v) => typeof v === 'string')
      .join('; ')
      .trim();
    return joined || undefined;
  }
  return undefined;
}

export async function buildStreamHttpErrorFromResponse(
  response: Response
): Promise<BuildStreamHttpError> {
  let text = '';
  try {
    text = await response.text();
  } catch {
    // Body unavailable (aborted, already read): the status still classifies it.
  }
  let parsed: Record<string, unknown> = {};
  try {
    const value: unknown = JSON.parse(text);
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      parsed = value as Record<string, unknown>;
    }
  } catch {
    // Not JSON (proxy error page): fall back to the status.
  }
  const upstreamError = firstString(parsed.error);
  const message =
    firstString(parsed.message) ??
    upstreamError ??
    (text.trim().slice(0, 300) || `Request failed (${response.status})`);
  const code = isBuildStreamErrorCode(parsed.code)
    ? parsed.code
    : codeForHttpStatus(response.status, `${upstreamError ?? ''} ${message}`);
  return new BuildStreamHttpError(response.status, code, message);
}

/**
 * `fetch` for `DefaultChatTransport`: throws a typed `BuildStreamHttpError` for a non-2xx
 * response instead of the SDK's plain `Error(responseText)`, so `onError` can read the code.
 */
/**
 * Header that ties one build-stream POST together across the browser, Nest, the AI API
 * (`trace_id`) and Chef logs (PROTOTYPER_STABILITY_TECH_PLAN.md P0.1).
 */
export const BUILD_STREAM_REQUEST_ID_HEADER = 'X-Request-Id';

export function newBuildStreamRequestId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
  );
}

export function fetchBuildStream(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return requestBuildStream(input, init);
}

/**
 * `fetchBuildStream` with a token refresh (FE-3): a 401 — the access token expired during a long
 * turn — refreshes once and retries the same request with the new token and the same request id.
 * Without `refreshAccessToken`, or when the refresh fails, the 401 surfaces as `SESSION_EXPIRED`.
 */
export function createBuildStreamFetch(
  options: { refreshAccessToken?: () => Promise<string | null> } = {}
): (input: RequestInfo | URL, init?: RequestInit) => Promise<Response> {
  return (input, init) => requestBuildStream(input, init, options.refreshAccessToken);
}

async function requestBuildStream(
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  refreshAccessToken?: () => Promise<string | null>
): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has(BUILD_STREAM_REQUEST_ID_HEADER)) {
    headers.set(BUILD_STREAM_REQUEST_ID_HEADER, newBuildStreamRequestId());
  }
  const requestId = headers.get(BUILD_STREAM_REQUEST_ID_HEADER);
  let response = await fetch(input, { ...init, headers });
  if (response.status === 401 && refreshAccessToken) {
    const token = await refreshAccessToken().catch(() => null);
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      response = await fetch(input, { ...init, headers });
    }
  }
  if (!response.ok) {
    // The id to search for in Nest, AI API and Chef logs when a founder reports this failure.
    console.warn('[Workbench] build-stream request failed', {
      requestId,
      status: response.status,
    });
    throw await buildStreamHttpErrorFromResponse(response);
  }
  return response;
}

/** The `data` of a `data-prototyperError` part, or null when it is not one. */
export function parseBuildStreamErrorData(data: unknown): BuildStreamError | null {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const record = data as Record<string, unknown>;
  if (!isBuildStreamErrorCode(record.code)) return null;
  const message = firstString(record.message) ?? USER_MESSAGES[record.code];
  return { code: record.code, message };
}

/** Classify whatever `useChat`'s `onError` received. */
export function classifyBuildStreamError(error: unknown): BuildStreamError {
  if (error instanceof BuildStreamHttpError) {
    return { code: error.code, message: error.message, status: error.status };
  }
  if (error instanceof TypeError) {
    // `fetch` rejects with TypeError for network failures (offline, CORS, DNS).
    return { code: 'CHEF_UNAVAILABLE', message: error.message };
  }
  const message = error instanceof Error ? error.message : String(error ?? '');
  // "context is too large" is Chef's own wording (generator.ts `isContextOverflowError` reply).
  if (
    /context (length|window)|context is too large|too many tokens|prompt is too long/i.test(message)
  ) {
    return { code: 'CONTEXT_OVERFLOW', message };
  }
  return { code: 'INTERNAL', message };
}

export function retryPolicyFor(code: BuildStreamErrorCode): BuildStreamRetryPolicy {
  switch (code) {
    case 'INSUFFICIENT_CREDITS':
    case 'NOT_OWNER':
    case 'VALIDATION_ERROR':
    case 'CONTEXT_OVERFLOW':
    case 'FIX_LIMIT_REACHED':
    case 'SESSION_EXPIRED':
      return 'none';
    default:
      return 'backoff';
  }
}

/** Delay before retry number `attempt` (0-based): 2 s, 4 s, then capped at 10 s. */
export function backoffDelayMs(attempt: number): number {
  return Math.min(2_000 * 2 ** Math.max(0, attempt), 10_000);
}

export function userMessageFor(code: BuildStreamErrorCode): string {
  return USER_MESSAGES[code] ?? USER_MESSAGES.INTERNAL;
}
